import React, { ReactNode, useState, useContext, createContext } from "react";
import { TFunction } from "i18next";
import i18next, { changeLanguage } from "../../i18n";

interface LanguageList {
  langId: number;
  languageName: string;
  languageCode: string;
  languageCountryFlag: string;
}

interface LanguageContextType {
  i18next: {
    dir(): string;
    changeLanguage: (lang: string) => Promise<TFunction>;
    language: string;
  };
  isLanguage: LanguageList | null;
  setLanguage: (value: LanguageList) => void;
  defaultLanguage: number | null;
  setDefaultLanguage: (value: number) => void;
  isAllLanguage: LanguageList[] | null;
  setAllLanguage: (value: LanguageList[]) => void;
  onClickLanguageChange: (language: string, langId: number) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isLanguage, setLanguage] = useState<LanguageList | null>(null);
  const [isAllLanguage, setAllLanguage] = useState<LanguageList[] | null>(null);
  const [defaultLanguage, setDefaultLanguage] = useState<number | null>(null);

  const onClickLanguageChange = async (language: string, langId: number) => {
    localStorage.setItem("lng", language);
    localStorage.setItem("langId", String(langId));
    await changeLanguage(language);
  };

  const contextValue: LanguageContextType = {
    i18next,
    isLanguage,
    setLanguage,
    defaultLanguage,
    setDefaultLanguage,
    isAllLanguage,
    setAllLanguage,
    onClickLanguageChange,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error(
      "useLanguageContext must be used within a LanguageContextProvider"
    );
  }
  return context;
};
